import { MonitorHeart as BloodPressureIcon } from "@mui/icons-material";
import { Card, CardContent, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import React, { useState, useMemo, useCallback } from "react";

import { activityApi } from "apis";
import {
  BloodPressureData,
  parseBloodPressureValue as parseBloodPressureValueHelper,
} from "helpers/bloodPressureHelpers";
import {
  buildDayData,
  buildWeekData,
  buildMonthData,
  build6MonthData,
  buildYearData,
  buildCustomData,
} from "helpers/chartDataHelpers";
import { TimeRange, getDateRangeForTimeRange } from "helpers/dateRangeHelpers";
import {
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
} from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";
import {
  ChartHeader,
  TimeRangeSelector,
  ChartRangeDisplay,
  BloodPressureScatterChart,
} from "./chart-components";

interface Props {
  patientId: string;
}

const BloodPressureChart: React.FC<Props> = ({ patientId }) => {
  const { colors } = useCelleraTheme();
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("1W");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /** 🗓️ Date range calculation */
  const getDateRange = useCallback(
    (timeRange: TimeRange, customDate?: Date) =>
      getDateRangeForTimeRange(timeRange, customDate),
    [],
  );

  const { startDate, endDate } = useMemo(
    () => getDateRange(selectedTimeRange, selectedDate || undefined),
    [getDateRange, selectedTimeRange, selectedDate],
  );

  /** 📊 Fetch blood pressure data */
  const { data: metricData, isLoading } = useQuery({
    queryKey: [
      "bloodPressureChart",
      patientId,
      selectedTimeRange,
      selectedDate,
    ],
    queryFn: async () => {
      if (!patientId) return null;
      // Always request daily data to get full range from individual readings
      const interval = MetricValuesAggregationRequestDtoIntervalEnum.Daily;

      const request: MetricValuesAggregationRequestDto = {
        metricCode:
          MetricValuesAggregationRequestDtoMetricCodeEnum.BloodPressure,
        healthProviderCode:
          MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
        interval,
        aggregateTypes: [
          MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
        ],
        startDate,
        endDate,
        orderBy: "ASC",
      };

      const response = await activityApi.getMetricValuesAggregatedByPatientId({
        patientId,
        metricValuesAggregationRequestDto: request,
      });
      return response;
    },
    enabled: !!patientId,
    refetchOnWindowFocus: false,
  });

  /** 🧮 Parse blood pressure values from API data */
  const parseBloodPressureValue = useCallback(
    parseBloodPressureValueHelper,
    [],
  );

  /** 🧮 Chart data builder */
  const chartData = useMemo(() => {
    if (!metricData?.data) return [];

    const dataMap: Record<string, BloodPressureData> = {};
    for (const d of metricData.data) {
      const key = format(new Date(d.startDate), "yyyy-MM-dd");
      const rawValue =
        d.valueTextData?.latest ||
        d.valueFloatData?.latest ||
        d.valueIntData?.latest ||
        d.valueJsonData?.latest;

      dataMap[key] = parseBloodPressureValue(rawValue);
    }

    const today = new Date();

    switch (selectedTimeRange) {
      case "1D":
        return buildDayData(today, dataMap);
      case "1W":
        return buildWeekData(today, dataMap);
      case "1M":
        return buildMonthData(today, dataMap);
      case "6M":
        return build6MonthData(today, dataMap);
      case "1Y":
        return buildYearData(today, dataMap);
      case "CUSTOM":
        return selectedDate ? buildCustomData(selectedDate, dataMap) : [];
      default:
        return [];
    }
  }, [metricData, selectedTimeRange, selectedDate, parseBloodPressureValue]);

  // Calculate range for both systolic and diastolic from ALL individual daily readings
  const { systolicRange, diastolicRange } = useMemo(() => {
    if (!metricData?.data) {
      return {
        systolicRange: { min: 0, max: 0 },
        diastolicRange: { min: 0, max: 0 },
      };
    }

    // Get all individual daily readings from the raw API data
    const allReadings = metricData.data.map((d) => {
      const rawValue =
        d.valueTextData?.latest ||
        d.valueFloatData?.latest ||
        d.valueIntData?.latest ||
        d.valueJsonData?.latest;
      return parseBloodPressureValue(
        rawValue as string | number | object | null | undefined,
      );
    });

    const systolicValues = allReadings
      .map((bp) => bp.systolic)
      .filter((v) => v > 0);
    const diastolicValues = allReadings
      .map((bp) => bp.diastolic)
      .filter((v) => v > 0);

    // Find the most recent day with actual data (not 0 values)
    const currentData = chartData
      .slice()
      .reverse()
      .find((d) => d.systolic > 0 || d.diastolic > 0);

    return {
      systolicRange: {
        min:
          systolicValues.length > 0
            ? Math.round(Math.min(...systolicValues))
            : 0,
        max:
          systolicValues.length > 0
            ? Math.round(Math.max(...systolicValues))
            : 0,
      },
      diastolicRange: {
        min:
          diastolicValues.length > 0
            ? Math.round(Math.min(...diastolicValues))
            : 0,
        max:
          diastolicValues.length > 0
            ? Math.round(Math.max(...diastolicValues))
            : 0,
      },
      currentSystolic: currentData ? Math.round(currentData.systolic) : 0,
      currentDiastolic: currentData ? Math.round(currentData.diastolic) : 0,
    };
  }, [metricData, chartData, parseBloodPressureValue]);

  const ranges: TimeRange[] = ["1D", "1W", "1M", "6M", "1Y"];

  if (isLoading) {
    return (
      <Card sx={{ borderRadius: 2, border: "1px solid #E8E8E9", p: 2 }}>
        <Typography variant="body2">Loading blood pressure data...</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: "1px solid #C6C5D5",
        p: 2,
        width: "100%",
        height: 516,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column", pb: 0 }}
      >
        <ChartHeader
          icon={<BloodPressureIcon />}
          title="Blood Pressure"
          colors={colors}
          isDatePickerOpen={isDatePickerOpen}
          setIsDatePickerOpen={setIsDatePickerOpen}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          setSelectedTimeRange={setSelectedTimeRange}
        />

        <TimeRangeSelector
          ranges={ranges}
          selectedTimeRange={selectedTimeRange}
          onTimeRangeChange={setSelectedTimeRange}
        />

        <ChartRangeDisplay
          rangeValue={`${systolicRange.min}-${systolicRange.max} / ${diastolicRange.min}-${diastolicRange.max} mmHg`}
        />

        <BloodPressureScatterChart
          chartData={chartData}
          selectedTimeRange={selectedTimeRange}
          colors={colors}
        />
      </CardContent>
    </Card>
  );
};

export default BloodPressureChart;
